import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Physics, RigidBody } from '@react-three/rapier'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

const PHYSICS_CONFIG = {
  defaultMass: 0,
}

function parsePhysicsType(userData)
{
  // Support new physics properties
  if (userData.physics_type)
  {
    const typeMap = {
      'DYNAMIC': 'dynamic',
      'STATIC': 'fixed',
      'KINEMATIC': 'kinematicPosition'
    }
    const type = typeMap[ userData.physics_type ] || 'fixed'
    // physics_weight is the new mass property
    const mass = parseFloat(userData.physics_weight || 0)
    return { type, mass }
  }

  const dynamicMass = parseInt(userData.Dynamic, PHYSICS_CONFIG.defaultMass)
  return {
    type: dynamicMass > 0 ? 'dynamic' : 'fixed',
    mass: dynamicMass,
  }
}

function isInvisible(userData)
{
  return userData.Invisible === 'true'
}

function isStatic(userData)
{
  return userData.Static === 'true'
}

function getAnimationNames(userData)
{
  return [
    ...(userData.OnSelectAnimations || []),
    ...(userData.OnPointerEnterAnimations || []),
    ...(userData.OnPointerExitAnimations || []),
    ...(userData.LoopingAnimations || []),
  ]
}


/**
 * Single physics rigid body component.
 */
function PhysicsRigidBody({
  obj,
  physicsType,
  mass,
  invisible,
  friction,
  restitution,
  callbacks,
  actions,
  rigidBodyRef
})
{
  // Sync animated objects with their physics pairs
  useFrame(() =>
  {
    if (!rigidBodyRef.current) return

    const { obj: sourceObj, actions: boundActions } = rigidBodyRef.current.userData || {}

    if (!sourceObj || !boundActions?.length) return

    boundActions.forEach(action =>
    {
      if (action?.isRunning())
      {
        rigidBodyRef.current.setTranslation(sourceObj.position, true)
        rigidBodyRef.current.setRotation(sourceObj.quaternion, true)
      }
    })
  })

  const userData = useMemo(() => ({
    obj,
    actions,
  }), [ obj, actions ])

  // Clone the object and reset its local transforms to identity
  // This is CRITICAL because the RigidBody wrapper is already positioned at the object's World Transform.
  // If we kept the clone's local transform, we would be applying the rotation/position twice.
  const clonedObj = useMemo(() =>
  {
    const clone = obj.clone()
    clone.position.set(0, 0, 0)
    clone.rotation.set(0, 0, 0)
    clone.quaternion.set(0, 0, 0, 1)
    return clone
  }, [ obj ])

  // Use SceneManager's calculated world transforms for initial state if available
  // Fallback to local if not (though SceneManager should have populated this)
  const initialPos = obj.userData?.worldPosition || obj.position
  const initialRot = obj.userData?.worldRotation || obj.quaternion

  return (
    <RigidBody
      type={physicsType}
      mass={mass}
      friction={friction}
      restitution={restitution}
      {...callbacks}
      userData={userData}
      ref={rigidBodyRef}
      position={initialPos}
      quaternion={initialRot}
    >
      <primitive
        object={clonedObj}
        position={[ 0, 0, 0 ]}
        visible={!invisible}
      />
    </RigidBody>
  )
}

/**
 * PhysicsObjects component manages all physics-enabled objects in the scene.
 * Creates rigid bodies for objects marked with Physics in userData.
 * 
 * @param {Object} props
 * @param {Object} props.sceneManager - The SceneManager instance
 * @param {Object} props.interactionManager - The InteractionManager instance
 * @param {boolean} props.isDebugging - Whether to show physics debug visualization
 */
export function PhysicsObjects({ sceneManager, interactionManager, isDebugging })
{
  const [ physicsData, setPhysicsData ] = useState(null)
  const rigidBodyRefs = useRef({})

  /**
   * Get animation actions bound to an object.
   */
  const getActions = useCallback((obj) =>
  {
    const boundActions = sceneManager.getBoundedActions(obj)
    const animationNames = getAnimationNames(obj.userData)

    if (boundActions.length > 0 || animationNames.length > 0)
    {
      const actions = [ ...boundActions ]

      animationNames.forEach(name =>
      {
        const action = sceneManager.getAnimationAction(name)
        if (action)
        {
          actions.push(action)
        }
      })

      return actions
    }

    return null
  }, [ sceneManager ])

  /**
   * Create event callbacks for physics object.
   */
  const getCallbacks = useCallback((obj) =>
  {
    const callbacks = {}
    const userData = obj.userData || {}

    const hasSelectTrigger = userData.OnSelectAnimations ||
      userData.mediaTrigger === 'OnSelect' ||
      userData.type === 'interactable'

    const hasEnterTrigger = userData.OnPointerEnterAnimations ||
      userData.mediaTrigger === 'OnPointerEnter' ||
      userData.type === 'interactable'

    const hasExitTrigger = userData.OnPointerExitAnimations ||
      userData.mediaTrigger === 'OnPointerExit' ||
      userData.type === 'interactable'

    if (hasSelectTrigger)
    {
      callbacks.onClick = (event) =>
      {
        event.object = obj
        interactionManager?.handleInteraction(event)
      }
    }

    if (hasEnterTrigger)
    {
      callbacks.onPointerEnter = (event) =>
      {
        event.object = obj
        interactionManager?.handlePointerEnter(event)
      }
    }

    if (hasExitTrigger)
    {
      callbacks.onPointerLeave = (event) =>
      {
        event.object = obj
        interactionManager?.handlePointerExit(event)
      }
    }

    return callbacks
  }, [ interactionManager ])

  // Process physics objects on mount
  useEffect(() =>
  {
    if (!sceneManager) return

    const physicsObjects = sceneManager.getPhysicsObjects()

    if (physicsObjects.length === 0)
    {
      setPhysicsData(null)
      return
    }

    let hasInvisible = false
    const processedObjects = []

    physicsObjects.forEach((obj, index) =>
    {
      const userData = obj.userData || {}
      const { type: physicsType, mass } = parsePhysicsType(userData)
      const invisible = isInvisible(userData)
      const staticObj = isStatic(userData)

      // Parse friction and restitution
      const friction = parseFloat(userData.physics_friction ?? 0.5)
      const restitution = parseFloat(userData.physics_restitution ?? 0.5)

      hasInvisible = hasInvisible || invisible
      obj.visible = false

      const actions = getActions(obj)
      const callbacks = getCallbacks(obj)

      // Create ref for this object
      if (!rigidBodyRefs.current[ index ])
      {
        rigidBodyRefs.current[ index ] = React.createRef()
      }

      // Check for legacy kinematic flag which overrides the parsed type
      const isKinematicLegacy = userData.Kinematic === 'true'
      const finalType = isKinematicLegacy ? 'kinematicPosition' : physicsType

      // Validity check: 
      // If we have physics_type, it's valid.
      // Else check legacy flags.
      const hasNewPhysics = 'physics_type' in userData
      const hasLegacyPhysics = (mass > 0) || isKinematicLegacy || staticObj

      if (hasNewPhysics || hasLegacyPhysics)
      {
        processedObjects.push({
          obj,
          physicsType: finalType,
          mass,
          friction,
          restitution,
          invisible,
          callbacks,
          actions,
          refIndex: index,
        })
      }
    })

    setPhysicsData({
      objects: processedObjects,
      includeInvisible: hasInvisible,
    })
  }, [ sceneManager, getActions, getCallbacks ])

  return (
    <>
      {physicsData && physicsData.objects.length > 0 && (
        <Physics debug={isDebugging} includeInvisible={physicsData.includeInvisible}>
          {physicsData.objects.map((item, index) => (
            <PhysicsRigidBody
              key={`physics-${item.obj.name || index}`}
              obj={item.obj}
              physicsType={item.physicsType}
              mass={item.mass}
              friction={item.friction}
              restitution={item.restitution}
              invisible={item.invisible}
              callbacks={item.callbacks}
              actions={item.actions}
              rigidBodyRef={rigidBodyRefs.current[ item.refIndex ]}
            />
          ))}
        </Physics>
      )}
    </>
  )
}

export default PhysicsObjects
